import { createRouter } from "@tanstack/react-router";
import { queryClient } from "@core/libs/react-query";
import { ContentError, type CommonKey } from "@core/components";
import { routeTree } from "./routeTree.gen";

export const router = createRouter({
  routeTree,
  // Shared with beforeLoad guards; the query cache is the source of truth for identity.
  context: { queryClient },
  defaultPreload: "intent",
  // A page that fails shows its error INSIDE the frame around it, not instead of the screen.
  defaultErrorComponent: ContentError,
  scrollRestoration: true,
  // Navigation cross-fades instead of cutting (browser View Transitions: GPU-composited, no layout
  // cost; a no-op where unsupported). Timing and reduced motion are set in theme.css.
  defaultViewTransition: true,
});

// Register the router instance globally so route APIs (params, search, links) are type-safe.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  /**
   * Per-route static metadata. `breadcrumb` is the app-header trail as `common` i18n keys, so a
   * screen declares its own crumbs and the shared <Breadcrumb/> never hard-codes a route map.
   * Typed as CommonKey, which makes a typo'd crumb a compile error rather than a raw key on
   * screen. Augmented here (not in core) because core may not import the route layer.
   */
  interface StaticDataRouteOption {
    breadcrumb?: readonly CommonKey[];
    /** The page title (`common` key): the document title and the focus announcement follow it. */
    title?: CommonKey;
    /**
     * One step of the frame's breadcrumb (`common` key), for the rebuilt staff pages: the trail is
     * the steps of every matched route, outermost first. A route whose loader returns `{ crumb }`
     * (a string, a cohort's name) adds that step after its own.
     */
    crumb?: CommonKey;
  }
}
